import React, { useState, useCallback } from 'react';

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  loading?: 'lazy' | 'eager';
  priority?: boolean;
  sizes?: string;
  webpSrc?: string;
  fallbackSrc?: string;
  onLoad?: () => void;
  onError?: () => void;
}

const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  className = '',
  width,
  height,
  loading = 'lazy',
  priority = false,
  sizes,
  webpSrc,
  fallbackSrc,
  onLoad,
  onError
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const handleLoad = useCallback(() => {
    setImageLoaded(true);
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback(() => {
    setImageError(true);
    onError?.();
  }, [onError]);

  // Generate responsive srcset for different screen sizes
  const generateSrcSet = useCallback((baseSrc: string) => {
    const sizes = [480, 768, 1024, 1280, 1440, 1920];
    return sizes.map(size => {
      // In a real app, you'd have image optimization service
      // For now, we'll use the original image
      return `${baseSrc} ${size}w`;
    }).join(', ');
  }, []);

  const effectiveLoading = priority ? 'eager' : loading;

  return (
    <div className={`relative ${className}`}>
      {/* Loading placeholder */}
      {!imageLoaded && !imageError && (
        <div 
          className="absolute inset-0 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"
          style={{ width, height }}
        />
      )}

      {/* Error fallback */}
      {imageError && fallbackSrc && (
        <img
          src={fallbackSrc}
          alt={alt}
          className="w-full h-full object-cover"
          loading={effectiveLoading}
          onLoad={handleLoad}
        />
      )}

      {/* Main image with WebP support */}
      {!imageError && (
        <picture>
          {webpSrc && (
            <source
              srcSet={generateSrcSet(webpSrc)}
              sizes={sizes}
              type="image/webp"
            />
          )}
          <img
            src={src}
            srcSet={generateSrcSet(src)}
            sizes={sizes}
            alt={alt}
            className={`w-full h-full object-cover transition-opacity duration-300 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            width={width}
            height={height}
            loading={effectiveLoading}
            onLoad={handleLoad}
            onError={handleError}
            {...(priority && { fetchPriority: 'high' as any })}
          />
        </picture>
      )}
    </div>
  );
};

export default OptimizedImage;
